import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FaCopy, FaRedoAlt, FaTrash } from 'react-icons/fa';
import { Icon } from '../common/Icon';

const MenuContainer = styled.div<{ $x: number; $y: number }>`
  position: fixed;
  left: ${props => props.$x}px;
  top: ${props => props.$y}px;
  background: #2a2a2a;
  border: 1px solid #444;
  border-radius: 6px;
  padding: 4px;
  min-width: 180px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  z-index: 1000;
  animation: fadeIn 0.15s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  background: transparent;
  border: none;
  color: #fff;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s;
  border-radius: 4px;

  &:hover {
    background: #444;
  }

  &:active {
    background: #555;
  }
`;

const MenuSeparator = styled.div`
  height: 1px;
  background: #444;
  margin: 4px 8px;
`;

const MenuLabel = styled.div`
  padding: 4px 12px;
  color: #888;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

interface WaveformContextMenuProps {
  x: number;
  y: number;
  trackName: string;
  onDuplicate: () => void;
  onLoop: () => void;
  onRemove: () => void;
  onClose: () => void;
}

export const WaveformContextMenu: React.FC<WaveformContextMenuProps> = ({
  x,
  y,
  trackName,
  onDuplicate,
  onLoop,
  onRemove,
  onClose,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const handleMenuAction = (action: () => void) => {
    action();
    onClose();
  };

  return (
    <MenuContainer ref={menuRef} $x={x} $y={y}>
      <MenuLabel>{trackName}</MenuLabel>
      <MenuSeparator />
      
      <MenuItem onClick={() => handleMenuAction(onDuplicate)}>
        <Icon icon={FaCopy} />
        Duplicate
      </MenuItem>
      
      <MenuItem onClick={() => handleMenuAction(onLoop)}>
        <Icon icon={FaRedoAlt} />
        Loop to End
      </MenuItem>
      
      <MenuSeparator />
      
      <MenuItem onClick={() => handleMenuAction(onRemove)}>
        <Icon icon={FaTrash} />
        Remove
      </MenuItem>
    </MenuContainer>
  );
};

export default WaveformContextMenu;